import { FormEventHandler, useEffect, useMemo, useRef, useState } from "react"
import { useGoogleMaps } from "../../../GoogleMaps"
import styled from "styled-components"
import { useGetGpsHistory } from "../../../../api/history"
import { GetGpsHistoryParams } from "../../../../types"
import { theme } from "../../../../styles/theme"

interface Props {
  apiParams: GetGpsHistoryParams
}
type PageRef = {
  gps: Record<string, {lat: number, lng: number}>,
  markers: google.maps.marker.AdvancedMarkerElement[],
  polyline: google.maps.Polyline | null,
}
export const Section_History_Googlemap = ({apiParams}: Props) => {
  const
  ref = useRef<PageRef>({
    gps: {
      daejeonCityHall: {lat: 36.3504, lng: 127.3848},
    },
    markers: [],
    polyline: null,
  }).current,
  mapDivRef = useRef<HTMLDivElement | null>(null),
  gpsDateRef = useRef<HTMLDivElement | null>(null),
  gm = useGoogleMaps(),
  [map, setMap] = useState<google.maps.Map | null>(null),
  {data: list} = useGetGpsHistory(apiParams),
  isLoaded = Array.isArray(list),
  listLength = isLoaded ? list.length : 0

  useEffect(() => {
    if (gm) {
      const mapDiv = mapDivRef.current
      if (mapDiv) {
        Promise.all(["maps", "marker"].map(lib => gm.importLibrary(lib)))
        .then(libs => {
          // map
          const map = new gm.Map(mapDiv, {
            mapId: "device_history",
            zoom: 16,
            center: ref.gps.daejeonCityHall,
            streetViewControl: false,
            mapTypeControl: false,
            gestureHandling: "cooperative",
            styles: [
              {
                featureType: "poi",
                stylers: [{ visibility: "off" }]
              }
            ]
          })
          setMap(map)
        })
      }
    }
  }, [])

  useMemo(() => {
    if (map && ref.markers) {
      for (const marker of ref.markers) marker.map = null
      ref.markers.length = 0
      ref.polyline?.setMap(null)
    }
  }, [map, apiParams])
  // 신규 list 데이터를 통해 초기값 노출
  useMemo(() => {
    if (map && list) {
      if (list.length) {
        const
        start = list[0],
        end = list[list.length - 1],
        startPin = new gm.marker.PinElement({
          background: "red",
          borderColor: "white",
          glyphColor: "white",
        }),
        endPin = new gm.marker.PinElement({
          background: "blue",
          borderColor: "white",
          glyphColor: "white",
        })

        ref.markers.push(new gm.marker.AdvancedMarkerElement({
          map,
          position: {lat: start.latitude, lng: start.longitude},
          title: String(start.time),
          content: startPin.element,
          zIndex: 2,
        }))
        ref.markers.push(new gm.marker.AdvancedMarkerElement({
          map,
          position: {lat: end.latitude, lng: end.longitude},
          title: String(end.time),
          content: endPin.element,
          zIndex: 1,
        }))

        const
        path: {lat: number, lng: number}[] = [],
        bounds = new gm.LatLngBounds()

        for (const point of list) {
          const position = {lat: point.latitude, lng: point.longitude}
          path.push(position)
          bounds.extend(position)
        }
        ref.polyline = new gm.Polyline({
          map,
          path,
          strokeColor: "red",
          strokeWeight: 2,
        })
        map.fitBounds(bounds, 20)
        if (gpsDateRef.current) gpsDateRef.current.textContent = new Date(start.time * 1000).toLocaleString('ko-KR', {timeZone: "UTC"})
      }
    }
  }, [map, list])

  const onChange: FormEventHandler = e => {
    try {
      // @ts-ignore
      const {target} = e
      if (target instanceof HTMLInputElement) {
        const {name, value} = target

        if (name == "track") {
          if (!list) return
          const index = Number(value)
          if (index < 0) return
          const startMarker = ref.markers[0]
          if (!startMarker) return
          const data = list[index]
          if (!data) return
          startMarker.position = {lat: data.latitude, lng: data.longitude}
          if (gpsDateRef.current) gpsDateRef.current.textContent = new Date(data.time * 1000).toLocaleString('ko-KR', {timeZone: "UTC"})
        }
      }

    } catch (e: any) {
      console.error(`Section_History_Googlemap.onChange > `, e)
    }
  }

  return (
    <SSection className="Googlemap" onChange={onChange}>
      <div ref={mapDivRef} />

      {isLoaded && list.length == 0 && <div className="no_gps">해당 주행에 경로 데이터가 없습니다</div>}

      <div className={`gps_date ${(listLength > 0) ? "show" : ""}`} ref={gpsDateRef}></div>

      {listLength > 1 && (
        <div className="locater">
          <input key={listLength} type="range" name="track" min={0} max={listLength - 1} step={1} defaultValue={0} />
        </div>
      )}
    </SSection>
  )
}

const SSection = styled.section`
  position: relative;

  & > div {
    z-index: 1;
  }
  & > .no_gps {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 20px;
    height: 50px;
    border-radius: 25px;
    background-color: ${theme.colors.black_bg_trans};
    pointer-events: none;
  }
  & > .gps_date {
    position: absolute;
    bottom: 45px;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 5px 10px;
    border-radius: 8px;
    font-size: 14px;
    visibility: hidden;
    &.show {
      visibility: visible;
    }
  }
  & > .locater {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    display: grid;
  }
`